import "./selectLand.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
const SELECT_LAND_SELECTOR = '[data-select-land]';

type SelectLandHook = (value: string, text: string) => void;

type SelectLandOptions = {
  prefix?: string;
  onChange?: SelectLandHook;
};

export class SelectLand {
  private readonly id: string;
  private static registry = new ComponentRegistry<SelectLand>('select-land');
  private readonly prefix: string;
  private readonly select: HTMLSelectElement;
  private readonly originalTexts: Map<HTMLOptionElement, string>;
  private readonly onChange?: SelectLandHook;

  private constructor(
    select: HTMLSelectElement,
    options: SelectLandOptions = {}
  ) {
    if (!(select instanceof HTMLSelectElement)) {
      throw new Error('SelectLand: Element must be a HTMLSelectElement');
    }

    this.id = SelectLand.registry.register(select, this);
    this.select = select;
    this.prefix = options.prefix ?? '';
    this.onChange = options.onChange;
    this.originalTexts = new Map();
    
    this.init();
  }

  private init(): void {
    this.storeOriginalTexts();
    this.applyPrefixToSelected();
    this.bindEvents();
  }

  private storeOriginalTexts(): void {
    Array.from(this.select.options).forEach(option => {
      this.originalTexts.set(option, option.text);
    });
  }

  private applyPrefixToSelected(): void {
    const selectedOption = this.select.options[this.select.selectedIndex];
    if (selectedOption && this.prefix) {
      const originalText = this.originalTexts.get(selectedOption) ?? selectedOption.text;
      selectedOption.textContent = `${this.prefix}${originalText}`;
    }
  }

  private bindEvents(): void {
    this.select.addEventListener('mousedown', this.handleOpen);
    this.select.addEventListener('focus', this.handleOpen);
    this.select.addEventListener('change', this.handleChange);
    this.select.addEventListener('blur', this.handleClose);
  }

  private handleOpen = (): void => {
    Array.from(this.select.options).forEach(option => {
      const originalText = this.originalTexts.get(option);
      if (originalText) {
        option.textContent = originalText;
      }
    });
  };

  private handleClose = (): void => {
    this.applyPrefixToSelected();
  };

  private handleChange = (e: Event): void => {
    const select = e.target as HTMLSelectElement;
    const selectedOption = select.options[select.selectedIndex];
    
    if (selectedOption && this.prefix) {
      const originalText = this.originalTexts.get(selectedOption) ?? selectedOption.text;
      selectedOption.textContent = `${this.prefix}${originalText}`;
    }
    
    if (this.onChange && selectedOption) {
      const originalText = this.originalTexts.get(selectedOption) ?? selectedOption.text;
      this.onChange(selectedOption.value, originalText);
    }
  };

  public static create(
    target: string | HTMLSelectElement | NodeListOf<HTMLElement> | HTMLElement[] = SELECT_LAND_SELECTOR,
    options: SelectLandOptions = {}
  ): SelectLand[] | null {
      const selects = ComponentRegistry.processTargets<SelectLand, HTMLElement>(
          SelectLand.registry,
          target,
          [HTMLSelectElement]
      ) as HTMLSelectElement[];
      if (!selects.length) return null;

      return selects.map(select => new SelectLand(select, options));
  }

  public static getSelect(id: string): SelectLand | null {
      return SelectLand.registry.get(id) ?? null;
  }

  public getValue(): string {
    return this.select.value;
  }

  public setValue(value: string): void {
    Array.from(this.select.options).forEach(option => {
      const originalText = this.originalTexts.get(option);
      if (originalText) {
        option.textContent = originalText;
      }
    });
    
    this.select.value = value;
    
    const selectedOption = this.select.options[this.select.selectedIndex];
    if (selectedOption && this.prefix) {
      const originalText = this.originalTexts.get(selectedOption) ?? selectedOption.text;
      selectedOption.textContent = `${this.prefix}${originalText}`;
    }
  }

  public destroy(): void {
    this.select.removeEventListener('mousedown', this.handleOpen);
    this.select.removeEventListener('focus', this.handleOpen);
    this.select.removeEventListener('change', this.handleChange);
    this.select.removeEventListener('blur', this.handleClose);
    
    Array.from(this.select.options).forEach(option => {
      const originalText = this.originalTexts.get(option);
      if (originalText) {
        option.textContent = originalText;
      }
    });
    
    this.originalTexts.clear();
    SelectLand.registry.unregister(this.id);
  }
}